import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, TextInput } from "@inceptionbg/ui-components";
import { Input } from "../../Elements/Input";
import { IFieldBook, IFieldEntry, IFieldLand } from "../../types/IFieldBook";
import {
  deleteEntry,
  getFieldBooksFieldByUuid,
  saveFieldEntry,
  saveFieldLand,
  updateFieldName,
} from "../../repos/FIeldBookRepo";
import { Loader } from "../../Elements/Loader";
import { FieldLandSection } from "./FieldLandSection";
import { FieldLandDialog } from "./FieldLandDialog";
import { FieldBookEntryDialog } from "./FieldBookEntryDialog";
import { Dialog } from "../../Elements/Dialog";
import { FieldBookEntriesTable } from "./FieldBookEntriesTable";
import { FarmContext } from "../../AppContent";
import { useParams, Params } from "react-router-dom";
import { SubSection } from "../../Components/SubSection";

export const FieldBookSingleFieldPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [field, setField] = useState<IFieldBook>();
  const [fieldLand, setFieldLand] = useState<Partial<IFieldLand>>({});
  const [fieldBookUuid, setFieldBookUuid] = useState("");

  const [fieldBookEntry, setFieldBookEntry] = useState<Partial<IFieldEntry>>(
    {}
  );
  const [entryFieldBookUuid, setEntryFieldBookUuid] = useState("");
  const [entryToDeleteUuid, setEntryToDeleteUuid] = useState("");
  const [isEditNameDialogOpen, setIsEditNameDialogOpen] = useState(false);
  const [fieldName, setFieldName] = useState("");

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;
  const childRef = useRef<{ refreshLandsSum: () => void }>(null);

  const { uuid } = useParams<Params>() as Params;

  const handleGetFieldBooks = useCallback(() => {
    if (farmUuid && uuid) {
      setIsLoading(true);
      getFieldBooksFieldByUuid(uuid)
        .then((data) => {
          setField(data);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [farmUuid, uuid]);

  useEffect(handleGetFieldBooks, [handleGetFieldBooks]);

  const onSubmitLand = () => {
    setIsLoading(true);
    saveFieldLand(fieldBookUuid, fieldLand)
      .then(() => {
        handleGetFieldBooks();
        setFieldBookUuid("");
        setFieldLand({});
        childRef.current?.refreshLandsSum();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const onSubmitEntry = () => {
    setIsLoading(true);
    saveFieldEntry({
      ...fieldBookEntry,
      treatmentAgent: fieldBookEntry.treatmentAgent
        ? { uuid: fieldBookEntry.treatmentAgent.uuid }
        : undefined,
      field: { uuid: entryFieldBookUuid },
    })
      .then(() => {
        handleGetFieldBooks();
        setEntryFieldBookUuid("");
        setFieldBookEntry({});
        childRef.current?.refreshLandsSum();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleEntryDelete = () => {
    setIsLoading(true);
    deleteEntry(entryToDeleteUuid)
      .then(() => {
        setEntryToDeleteUuid("");
        setEntryFieldBookUuid("");
        setFieldBookEntry({});
        handleGetFieldBooks();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleSaveFieldName = () => {
    if (field?.uuid && fieldName.trim()) {
      setIsLoading(true);
      updateFieldName(field.uuid, fieldName.trim())
        .then(() => {
          setIsEditNameDialogOpen(false);
          handleGetFieldBooks();
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  };

  const handleCloseEditNameDialog = () => {
    setIsEditNameDialogOpen(false);
    setFieldName("");
  };

  return (
    <div className="page-sub-section">
      <div className="page-section">
        <div className="page-section-header flex align-center">
          <h1 style={{ flex: 1 }}>{field?.name}</h1>
          <Button
            label={t("EditFieldName")}
            size="s"
            outlined
            onClick={() => {
              setFieldName(field?.name || "");
              setIsEditNameDialogOpen(true);
            }}
            disabled={isLoading}
          />
        </div>
      </div>
      {isLoading ? (
        <Loader isLoading>
          <div className="pt-5 full-width" />
        </Loader>
      ) : (
        field && (
          <div key={field.uuid} className="sub-section-content">
            <div className="form-container mt-3">
              <FieldLandSection
                fieldLands={field.fieldLands}
                ref={childRef}
                refreshFieldBooks={handleGetFieldBooks}
              />
            </div>
            <div className="flex justify-right mt-3">
              <Button
                size="s"
                label={t("FieldLandAdd")}
                primary
                onClick={() => setFieldBookUuid(field.uuid)}
              />
            </div>
            <FieldBookEntriesTable
              fieldUuid={field.uuid}
              setSelectedItem={(item) => {
                if (item) {
                  setEntryFieldBookUuid(item.field?.uuid || "");
                  setFieldBookEntry(item);
                }
              }}
              actionButton={
                <Button
                  label={t("AddEntry")}
                  outlined
                  size="s"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEntryFieldBookUuid(field.uuid);
                  }}
                />
              }
            />
            <FieldLandDialog
              isOpen={!!fieldBookUuid}
              onClose={() => {
                setFieldBookUuid("");
                setFieldLand({});
              }}
              data={fieldLand}
              setData={setFieldLand}
              onSubmit={onSubmitLand}
            />
            <FieldBookEntryDialog
              isOpen={!!entryFieldBookUuid}
              onClose={() => {
                setEntryFieldBookUuid("");
                setFieldBookEntry({});
              }}
              data={fieldBookEntry}
              setData={setFieldBookEntry}
              onSubmit={onSubmitEntry}
              entryFieldBookUuid={entryFieldBookUuid}
              onDelete={setEntryToDeleteUuid}
            />
            {field.fieldLands?.length > 0 && (
              <div className="flex justify-right mt-3">
                <Button
                  label={t("AddEntry")}
                  outlined
                  size="s"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEntryFieldBookUuid(field.uuid);
                  }}
                />
              </div>
            )}
          </div>
        )
      )}

      {field && (
        <SubSection title={t("Reports")}>
          <div className="flex gap-2">
            <Button
              label={t("ProfitLossReport")}
              size="s"
              outlined
              onClick={() =>
                window.open(`/fields-book/${uuid}/profit-loss-report`, "_blank")
              }
            />
          </div>
        </SubSection>
      )}

      <Dialog
        isOpen={!!entryToDeleteUuid}
        onClose={() => setEntryToDeleteUuid("")}
        desc={t("DeleteEntryDesc")}
        confirmButton={{
          label: t("Delete"),
          onClick: handleEntryDelete,
        }}
        cancelButton={{ onClick: () => setEntryToDeleteUuid("") }}
      />

      <Dialog
        isOpen={isEditNameDialogOpen}
        onClose={handleCloseEditNameDialog}
        title={t("EditFieldName")}
        confirmButton={{
          onFormSubmit: handleSaveFieldName,
          label: t("Confirm"),
          disabled: !fieldName.trim() || isLoading,
        }}
        cancelButton={{ onClick: handleCloseEditNameDialog }}
        size="m"
        isLoading={isLoading}
      >
        <Input
          component={{
            label: t("Name"),
            input: (
              <TextInput value={fieldName} setValue={setFieldName} autoFocus />
            ),
          }}
          edit
        />
      </Dialog>
    </div>
  );
};
